import React, { useState } from 'react'
import { C, ui, muteColor } from '../theme'

const h = ui.h
const input = ui.input
const btn = ui.btn
const btnGhost = ui.btnGhost
const row = ui.row

// Commander roster tab (Rules: "Optional rule - Commanders" + "Commanders at the campaign
// level"). Players manage their own faction's commanders; the DM sees and manages all.
// Point-buy math mirrors the server (src/engine/commanders.js) via the constants the
// commanders payload carries, so the form can show spent/budget live — the server still
// validates on save.

const ATTRS = [['strategic', 'Strategic'], ['inspirational', 'Inspirational'], ['siege', 'Siege']]
const STATUS_LABEL = { active: 'active', exposed: 'exposed', captured: 'captured', dead: 'dead' }

// budget/spent for a spec under the server's point-buy constants. priorSubs = substitutes the
// faction created before this commander.
const calcPoints = (pb, spec, priorSubs) => {
  const bg = pb && pb.backgrounds && pb.backgrounds[spec.type]
  if (!bg) return null
  let budget = bg.points + (spec.isChief ? pb.chiefBonus : 0)
  if (spec.isSubstitute) budget = Math.max(pb.substituteMin, budget - (priorSubs + 1))
  const spent = ATTRS.reduce((s, [k]) => s + (pb.attrCost[k] || 1) * (Math.max(0, Number(spec[k]) || 0) - bg.freeEach), 0)
  const overCap = ATTRS.find(([k]) => (Number(spec[k]) || 0) > bg.maxAttr || (Number(spec[k]) || 0) < bg.freeEach)
  return { spent, budget, maxAttr: bg.maxAttr, freeEach: bg.freeEach, overCap: overCap ? overCap[1] : null }
}

// Editable spec form (shared by create + edit). `spec` = { name, type, strategic,
// inspirational, siege, isChief, isSubstitute }; onChange(patch) merges.
function SpecForm({ pb, spec, priorSubs, onChange, chiefTakenBy }) {
  const pts = calcPoints(pb, spec, priorSubs)
  const over = pts && (pts.spent > pts.budget || pts.overCap)
  return (
    <>
      <input style={{ ...input, width: '100%' }} placeholder="name" value={spec.name}
        onChange={e => onChange({ name: e.target.value })} />
      <select style={{ ...input, width: '100%' }} value={spec.type || ''} onChange={e => onChange({ type: e.target.value || null })}>
        <option value="">background…</option>
        {pb && Object.entries(pb.backgrounds).map(([k, bg]) => (
          <option key={k} value={k}>{bg.label} ({bg.points} pts, max {bg.maxAttr})</option>
        ))}
      </select>
      {spec.type && (
        <>
          <div style={{ display: 'flex', gap: 4 }}>
            {ATTRS.map(([k, label]) => (
              <label key={k} style={{ flex: 1, fontSize: 10, opacity: 0.85, textAlign: 'center' }}>
                {label}{k === 'inspirational' ? ' (×2)' : ''}
                <input style={{ ...input, marginBottom: 0, padding: 2, textAlign: 'center' }} type="number"
                  min={pts ? pts.freeEach : 0} max={pts ? pts.maxAttr : 9}
                  value={spec[k]} onChange={e => onChange({ [k]: Number(e.target.value) })} />
              </label>
            ))}
          </div>
          {pts && (
            <div style={{ fontSize: 11, margin: '3px 0', color: over ? C.crimson : C.inkSoft }}>
              {pts.spent} / {pts.budget} points spent
              {pts.overCap ? ` — ${pts.overCap} is outside ${pts.freeEach}–${pts.maxAttr}` : ''}
              {spec.isSubstitute ? ` · substitute #${priorSubs + 1}` : ''}
            </div>
          )}
        </>
      )}
      <div style={{ ...row, marginBottom: 4 }}>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={!!spec.isChief} onChange={e => onChange({ isChief: e.target.checked })} />
          Chief (+{pb ? pb.chiefBonus : 2} pts)
        </label>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={!!spec.isSubstitute} onChange={e => onChange({ isSubstitute: e.target.checked })} />
          Substitute
        </label>
      </div>
      {spec.isChief && chiefTakenBy && (
        <div style={{ fontSize: 11, color: C.crimson, marginBottom: 4 }}>This faction's chief is already {chiefTakenBy}.</div>
      )}
    </>
  )
}

export default function CommandersPanel({
  isDm, canAct, factions, myFactionId, armies, data,
  onCreate, onPatch, onDelete,
}) {
  const [editingId, setEditingId] = useState(null)
  const [draft, setDraft] = useState(null)        // spec being edited (for editingId)
  const [assignTo, setAssignTo] = useState('')    // army id chosen in the edit assign dropdown
  const [creating, setCreating] = useState(false)
  const [createSpec, setCreateSpec] = useState(null)

  const pb = data && data.pointBuy
  const commanders = (data && data.commanders) || []
  const subsOf = (fid) => (data && data.substitutesByFaction && data.substitutesByFaction[String(fid)]) || 0
  const ownArmies = (fid) => armies.filter(a => !a.fog && a.faction_id === fid)
  const chiefOf = (fid, exceptId) => commanders.find(c => c.faction_id === fid && c.is_chief && c.status !== 'dead' && c.id !== exceptId)

  const whereabouts = (c) => {
    if (c.status === 'dead') return '☠ dead'
    if (c.status === 'captured') return '⛓ captured'
    if (c.in_transit_to_army_id) return `→ riding to ${c.transit_army_name || 'an army'} (arrives day ${c.arrive_day})`
    if (c.army_id) return `with ${c.army_name || 'an army'}${c.army_tile_name ? ` at ${c.army_tile_name}` : c.army_row != null ? ` at (${c.army_row},${c.army_col})` : ''}`
    return 'at court (unassigned)'
  }
  const abilityLine = (c) => c.type
    ? `${pb && pb.backgrounds[c.type] ? pb.backgrounds[c.type].label : c.type} · S${c.strategic_ability} I${c.inspirational_ability} G${c.siege_ability}`
    : 'no background yet — edit to spec'

  const startEdit = (c) => {
    setEditingId(c.id)
    setAssignTo(c.army_id != null ? String(c.army_id) : '')
    setDraft({
      name: c.name, type: c.type, strategic: c.strategic_ability, inspirational: c.inspirational_ability,
      siege: c.siege_ability, isChief: !!c.is_chief, isSubstitute: !!c.is_substitute,
      status: c.status, hd: c.hd,
    })
  }
  const saveEdit = async (c) => {
    const ok = await onPatch(c.id, {
      name: draft.name, type: draft.type, strategic: draft.strategic, inspirational: draft.inspirational,
      siege: draft.siege, isChief: draft.isChief, isSubstitute: draft.isSubstitute,
      ...(isDm && draft.status !== c.status ? { status: draft.status } : {}),
      ...(isDm && Number(draft.hd) !== c.hd ? { hd: Number(draft.hd) } : {}),
    })
    if (ok) { setEditingId(null); setDraft(null) }
  }
  const saveAssign = async (c) => {
    const armyId = assignTo === '' ? null : Number(assignTo)
    if ((c.army_id ?? null) === armyId && !c.in_transit_to_army_id) return
    const ok = await onPatch(c.id, { armyId })
    if (ok) { setEditingId(null); setDraft(null) }
  }

  const blankCreate = (fid) => ({ factionId: fid, name: '', type: null, strategic: 0, inspirational: 0, siege: 0, isChief: false, isSubstitute: false, armyId: '' })
  const submitCreate = async () => {
    const ok = await onCreate({
      factionId: createSpec.factionId, name: createSpec.name, type: createSpec.type,
      strategic: createSpec.strategic, inspirational: createSpec.inspirational, siege: createSpec.siege,
      isChief: createSpec.isChief, isSubstitute: createSpec.isSubstitute,
      armyId: createSpec.armyId === '' ? null : Number(createSpec.armyId),
    })
    if (ok) { setCreating(false); setCreateSpec(null) }
  }

  const visibleFactions = isDm ? factions : factions.filter(f => f.id === myFactionId)

  return (
    <>
      <div style={h}>Commanders</div>
      <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 8 }}>
        An army needs a commander in the field: without one it can only march to a friendly stronghold
        (or raid beside a commanded force), and after 2 leaderless days it abandons its orders and heads
        home. Transfers between armies take ~1 day per 6 areas — except on day 1, when unassigned
        commanders deploy anywhere instantly.
      </div>
      {!data && <div style={{ fontSize: 12, opacity: 0.6 }}>Loading commanders…</div>}
      {data && visibleFactions.map(f => {
        const list = commanders.filter(c => c.faction_id === f.id)
        return (
          <div key={f.id} style={{ marginBottom: 10 }}>
            {isDm && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '6px 0 4px' }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: muteColor(f.color), display: 'inline-block' }} />
                <b style={{ fontSize: 12.5 }}>{f.name}</b>
                <span style={{ fontSize: 11, opacity: 0.55 }}>({list.length})</span>
              </div>
            )}
            {list.length === 0 && <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 4 }}>No commanders yet.</div>}
            {list.map(c => (
              <div key={c.id} style={{
                padding: '6px 8px', borderRadius: 6, marginBottom: 4,
                background: editingId === c.id ? C.highlight : C.paperRaised,
                border: `1px solid ${editingId === c.id ? C.gold : C.rule}`,
                opacity: c.status === 'dead' ? 0.55 : 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: canAct ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (!canAct) return
                    if (editingId === c.id) { setEditingId(null); setDraft(null) } else startEdit(c)
                  }}>
                  <b style={{ flex: 1, fontSize: 13 }}>{c.is_chief ? '★ ' : ''}{c.name}</b>
                  <span style={{ fontSize: 10.5, opacity: 0.65 }}>{STATUS_LABEL[c.status] || c.status}{c.status !== 'dead' ? ` · ${c.hd} HD` : ''}</span>
                </div>
                <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>{abilityLine(c)}</div>
                <div style={{ fontSize: 11, opacity: 0.65 }}>{whereabouts(c)}{c.is_substitute ? ' · substitute' : ''}</div>

                {editingId === c.id && draft && (
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${C.rule}` }}>
                    <SpecForm pb={pb} spec={draft} onChange={(p) => setDraft(d => ({ ...d, ...p }))}
                      priorSubs={Math.max(0, subsOf(c.faction_id) - (c.is_substitute ? 1 : 0))}
                      chiefTakenBy={draft.isChief && !c.is_chief && chiefOf(c.faction_id, c.id) ? chiefOf(c.faction_id, c.id).name : null} />
                    {isDm && (
                      <div style={{ ...row, marginBottom: 4 }}>
                        <select style={{ ...input, flex: 1, marginBottom: 0 }} value={draft.status}
                          onChange={e => setDraft(d => ({ ...d, status: e.target.value }))}>
                          {Object.keys(STATUS_LABEL).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <input style={{ ...input, width: 64, marginBottom: 0 }} type="number" min={0} title="HD"
                          value={draft.hd} onChange={e => setDraft(d => ({ ...d, hd: e.target.value }))} />
                      </div>
                    )}
                    <div style={{ ...row, marginBottom: 6 }}>
                      <button style={{ ...btn, flex: 1 }} onClick={() => saveEdit(c)}>Save</button>
                      {isDm && <button style={{ ...btnGhost, color: C.crimson }} onClick={() => onDelete(c.id)}>Delete</button>}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 2 }}>Assignment</div>
                    <div style={row}>
                      <select style={{ ...input, flex: 1, marginBottom: 0 }} value={assignTo} onChange={e => setAssignTo(e.target.value)}>
                        <option value="">— at court (unassigned) —</option>
                        {ownArmies(c.faction_id).map(a => (
                          <option key={a.id} value={String(a.id)}>{a.name}{a.tile_name ? ` @ ${a.tile_name}` : a.tile_row != null ? ` @ (${a.tile_row},${a.tile_col})` : ''}</option>
                        ))}
                      </select>
                      <button style={btnGhost} disabled={c.status !== 'active' && assignTo !== ''} onClick={() => saveAssign(c)}>Send</button>
                    </div>
                    {c.status !== 'active' && <div style={{ fontSize: 10.5, opacity: 0.55, marginTop: 2 }}>Only an active commander can travel.</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      })}

      {canAct && data && (
        creating && createSpec ? (
          <div style={{ padding: 8, background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 6 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>New commander</div>
            {isDm && (
              <select style={{ ...input, width: '100%' }} value={createSpec.factionId}
                onChange={e => setCreateSpec(s => ({ ...s, factionId: Number(e.target.value), armyId: '' }))}>
                {factions.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            )}
            <SpecForm pb={pb} spec={createSpec} onChange={(p) => setCreateSpec(s => ({ ...s, ...p }))}
              priorSubs={subsOf(createSpec.factionId)}
              chiefTakenBy={createSpec.isChief && chiefOf(createSpec.factionId) ? chiefOf(createSpec.factionId).name : null} />
            <select style={{ ...input, width: '100%' }} value={createSpec.armyId}
              onChange={e => setCreateSpec(s => ({ ...s, armyId: e.target.value }))}>
              <option value="">no army yet (at court)</option>
              {ownArmies(createSpec.factionId).map(a => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
            </select>
            <div style={row}>
              <button style={{ ...btn, flex: 1 }} onClick={submitCreate}>Create</button>
              <button style={btnGhost} onClick={() => { setCreating(false); setCreateSpec(null) }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button style={{ ...btnGhost, width: '100%' }}
            onClick={() => { setCreating(true); setCreateSpec(blankCreate(isDm ? (factions[0] && factions[0].id) : myFactionId)) }}>
            + New commander
          </button>
        )
      )}
    </>
  )
}
